import React, { useCallback, useMemo } from "react";
import { useField } from "formik";
import { DirectorySort } from "@common/constants/counterparty";
import { RegisteredCounterpartyIncludeScope } from "@common/helpers/counterparties";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import {
    CounterpartyCombobox,
    CounterpartyComboboxValue,
} from "@components/transactions/TransactionSplitV2/CounterpartyCombobox";
import { useCounterpartiesQuery } from "@api/counterparty.api";
import { TransactionRuleActionFormSchema } from "../constants";

interface Props {
    index: number;
    controlButtonSize?: TriggerButtonProps["size"];
}

// Mirrors CounterpartyActionForm: the customer is a counterparty (the party
// being billed), just persisted under `customerId` instead of
// `counterpartyId`.
export const CustomerActionForm: React.FC<Props> = ({
    index,
    controlButtonSize,
}) => {
    const [field, , helpers] = useField<
        TransactionRuleActionFormSchema["value"]
    >(`actions.${index}.value`);
    const { data: counterparties } = useCounterpartiesQuery({
        sort: DirectorySort.NAME_ASC,
        includeRegistered: RegisteredCounterpartyIncludeScope.USED,
        includeUsageStats: false,
    });

    const selectedCustomer = useMemo(() => {
        const customerId = field.value?.customerId;
        if (!customerId) {
            return undefined;
        }

        return counterparties?.data?.find(
            (row) => row.counterparty.id === customerId,
        )?.counterparty;
    }, [counterparties?.data, field.value?.customerId]);

    const onChange = useCallback(
        (counterparty: CounterpartyComboboxValue | undefined) => {
            helpers.setValue({
                customerId: counterparty?.id,
            });
        },
        [helpers],
    );

    return (
        <CounterpartyCombobox
            selectedCounterparty={selectedCustomer}
            onChange={onChange}
            hideFooter
            placeholder="Select billable customer"
            searchPlaceholder="Search billable customers..."
            triggerButtonProps={{
                variant: "primary",
                size: controlButtonSize ?? "md",
            }}
        />
    );
};
